// SmsModal.jsx
import React, { useEffect, useState } from "react";

/**
 * Props:
 *  - isOpen: boolean
 *  - name: string (recipient name)
 *  - phone: string (recipient phone)
 *  - onSend: async (payload) => Promise
 *  - onClose: () => void
 *
 * Simple, minimal layout matching the screenshot.
 */
const SmsModal = ({ isOpen, name = "", phone = "", onSend, onClose }) => {
  const CHAR_LIMIT = 80;
  const [toNumber, setToNumber] = useState(phone || "");
  const [recipient, setRecipient] = useState(name || "");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setToNumber(phone || "");
    setRecipient(name || "");
    setMessage("");
    setError("");
    setSending(false);
  }, [isOpen, phone, name]);

  if (!isOpen) return null;

  const remaining = CHAR_LIMIT - message.length;

  const handleSend = async () => {
    setError("");
    if (!toNumber || !/^\d{7,15}$/.test(toNumber.replace(/\s+/g, ""))) {
      setError("Enter a valid mobile number");
      return;
    }
    if (!message.trim()) {
      setError("Message cannot be empty");
      return;
    }

    const payload = {
      to: toNumber.trim(),
      name: recipient.trim(),
      message: message.trim(),
      ts: new Date().toISOString(),
    };

    try {
      setSending(true);
      if (onSend) await onSend(payload);
      setSending(false);
      if (onClose) onClose();
    } catch (err) {
      console.error(err);
      setError("Failed to send. Try again.");
      setSending(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-start justify-center p-6 bg-black bg-opacity-30"
    >
      <div className="w-full max-w-3xl bg-white border border-gray-300 rounded-sm shadow-sm">
        {/* Thin top border like screenshot */}
        <div className="border-b border-gray-300 px-4 py-2 text-sm font-medium">
          Send To - <span className="font-semibold text-gray-700">{recipient || "Recipient"}</span>
        </div>

        {/* content area with a large empty left space and inputs aligned to right like screenshot */}
        <div className="px-6 py-6">
          <div className="flex items-start">
            {/* left empty area to mimic screenshot spacing */}
            <div className="flex-1"></div>

            {/* right column (inputs) */}
            <div className="w-full max-w-2xl">
              {/* mobile input row */}
              <div className="flex items-center gap-4 mb-3">
                <div className="w-36 text-sm text-gray-700"> </div>
                <input
                  type="tel"
                  value={toNumber}
                  onChange={(e) => setToNumber(e.target.value)}
                  className="px-2 py-1 border border-gray-300 rounded-sm w-48 text-sm"
                />
              </div>

              {/* dear + name row */}
              <div className="flex items-center gap-4 mb-4">
                <div className="w-36 text-sm text-gray-700">Dear</div>
                <input
                  type="text"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  className="px-2 py-1 border border-gray-300 rounded-sm flex-1 text-sm"
                />
                <div className="text-sm text-gray-700">,</div>
              </div>

              {/* message input */}
              <div className="flex items-center gap-4 mb-4">
                <div className="w-36"></div>
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder=""
                  maxLength={CHAR_LIMIT * 2}
                  className="px-2 py-1 border border-gray-300 rounded-sm flex-1 text-sm"
                />
                <div className="w-12 text-sm text-gray-500 text-right">({CHAR_LIMIT})</div>
              </div>

              {error && (
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-36" />
                  <div className="text-sm text-red-600">{error}</div>
                </div>
              )}

              {/* buttons centered below inputs */}
              <div className="flex items-center gap-4 mt-2">
                <div className="w-36" />
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleSend}
                    disabled={sending}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-sm text-sm ${
                      sending ? "bg-gray-300 text-gray-600 cursor-not-allowed" : "bg-green-600 text-white"
                    }`}
                  >
                    {/* small square icon to visually echo screenshot */}
                    <span className="bg-white px-1 py-0.5 rounded-sm text-xs">📩</span>
                    {sending ? "Sending..." : "SEND"}
                  </button>

                  <button
                    onClick={onClose}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-gray-300 text-sm text-gray-700 bg-white"
                  >
                    CLOSE
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* thin footer border */}
        <div className="h-3" />
      </div>
    </div>
  );
};

export default SmsModal;
